// frontend/src/services/optimizationService.ts
// v103.0 - SYSTEM PULSE & AUTOMATION TRIGGER

import axios from 'axios';
import axiosInstance from '../config/axiosInstance';
import type { ShapExplanation } from '../types/shapTypes';

// --- INTERFACES SISTEMA (NUEVAS) ---

export interface SystemModuleStatus {
  status: 'pending' | 'running' | 'success' | 'failed' | 'error';
  last_run_at: string | null; // ISO Date
  next_scheduled_run: string | null; // ISO Date
}

export interface SmartAction {
  can_trigger: boolean;
  reason: 'COLD_START' | 'NEW_DATA_AVAILABLE' | 'SYSTEM_UP_TO_DATE' | 'JOB_RUNNING' | 'NO_DATA';
}

export interface SystemPulseResponse {
  tier: string;
  global_status: 'idle' | 'processing' | 'error';
  modules: {
    DATA_INGEST: SystemModuleStatus;
    PRICING_ENGINE: SystemModuleStatus;
    DEMAND_FORECAST: SystemModuleStatus;
    FUTURE_SIMULATION: SystemModuleStatus;
  };
  smart_action: SmartAction;
}

export interface AutomationTriggerResponse {
  status: 'started';
  mode: string;
}

export interface OptimizationCacheStatusResponse {
  status: string;
  message?: string | null;
}

// --- FIN INTERFACES SISTEMA ---

// --- Sub-modelos de Explicación ---
export interface MpExplanationDetails {
  explanation_type: "MP";
  anchor_explanation: ShapExplanation;
  adjustment_explanation: ShapExplanation | null;
}

export interface F4ExplanationDetails {
  explanation_type: "F4";
  f4_explanation: ShapExplanation;
}

export interface NewProductAttributes {
  standard_cost: number;
  product_category?: string | null;
  color?: string | null;
  weight_kg?: number | null;
}

export interface NewClientAttributes {
  region?: string | null;
}

export interface PricingTier {
  quantity: number;
  suggested_price: number;
  suggested_discount: number; // 0.25 para 25%
  unit_margin: number;
}

export interface OptimizationInput {
  client_id: string;
  product_id?: string | null;
  coste_actual_existente?: number | null;
  quantity?: number | null;
  generate_pricing_matrix?: boolean;
  margin_override_percentage?: number | null;
  price_override?: number | null;
  is_new_product: boolean;
  new_product_attributes?: NewProductAttributes | null;
  is_new_client: boolean;
  new_client_attributes?: NewClientAttributes | null;
  strategy_profile?: string | null;
  optimisation_level_override?: number | null;
  strategy_sensitivity?: number | null;
}

export interface OptimizationOutput {
  optimization_id?: string | null;
  margen_sugerido: number | null;
  precio_sugerido: number | null;
  pricing_matrix?: PricingTier[] | null;
  list_price_used?: number | null;
  base_recomendacion: string | null;
  explanation_narrative: string[] | null;
  advertencias: string[] | null;
  tactical_intelligence: Record<string, string> | null;
  optimization_level_applied: "MP" | "F4" | "F5" | "Error" | null;
  applied_strategy_profile: string | null;
  final_applied_delta_margin: number | null;
  shap_analysis_available: boolean;
  explanation_details: MpExplanationDetails | F4ExplanationDetails | null;
  coste_utilizado: number | null;
  bhs_source: string | null;
  base_historical_solid_cp: number | null;
  mei_markup_mp_plus_mde: number | null;
  sor_signal_mei_vs_bhscp: number | null;
  sor_post_elasticity: number | null;
  raw_mp_markup_absolute: number | null;
  raw_mde_delta_predicted: number | null;
  elasticity_source: string | null;
  elasticity_value_used: number | null;
  is_elasticity_reliable: boolean | null;
  f4_anchor_source: string | null;
  f4_anchor_value: number | null;
  f4_predicted_markup: number | null;
}

export interface NarrativeResponse {
  status: 'pending' | 'ready' | 'error';
  narrative?: string[] | null;
  error_detail?: string | null;
}

export interface RetrainingStatus {
  status: 'not_found' | 'running' | 'success' | 'success_with_warnings' | 'error';
  log_id: string | null;
  started_at: string | null;
  finished_at: string | null;
  metrics: Record<string, any> | null;
  error_message: string | null;
  can_train: boolean;
  next_training_available_at: string | null;
}

export interface RetrainingJobResponse {
  message: string;
  log_id?: string;
}

export interface AttributeSchema {
  name: string;
  label: string;
  type: 'select' | 'text' | 'number';
  options?: string[];
  required?: boolean;
}

export interface ProductAttributesFormSchemaAPIResponse {
  success: boolean;
  data?: { new_product_features: AttributeSchema[]; };
  message?: string;
}

export interface ClientAttributesFormSchemaAPIResponse {
  success: boolean;
  data?: { new_client_features: AttributeSchema[]; };
  message?: string;
}

// --- API CALLS ---

export const getSystemPulse = async (): Promise<SystemPulseResponse> => {
  try {
    const response = await axiosInstance.get<SystemPulseResponse>('/system/pulse');
    return response.data;
  } catch (error: any) {
    console.error("[API] Error fetching system pulse:", error);
    throw new Error('Error de conexión con el núcleo del sistema.');
  }
};

export const triggerAutomationCycle = async (cronSecret: string): Promise<AutomationTriggerResponse> => {
  try {
    // Usamos el endpoint del sistema que invoca al orquestador v4.0
    const response = await axiosInstance.post<AutomationTriggerResponse>(
      '/system/cron/trigger-automation', 
      {}, 
      { headers: { 'X-Cron-Secret': cronSecret } } // Se requiere el secreto aunque sea interno
    );
    return response.data;
  } catch (error: any) {
    console.error("[API] Error triggering automation:", error);
    throw new Error('No se pudo iniciar el ciclo de automatización.');
  }
};

export const getNarrative = async (optimizationId: string): Promise<NarrativeResponse> => {
  try {
    const response = await axiosInstance.get<NarrativeResponse>(`/narrative/${optimizationId}`);
    return response.data;
  } catch (error: any) {
    console.error(`Error narrative ${optimizationId}:`, error);
    throw error;
  }
};

export const optimizePrice = async (input: OptimizationInput, explain: boolean): Promise<OptimizationOutput> => {
  try {
    const url = explain ? '/optimize?explain=true' : '/optimize';
    const response = await axiosInstance.post<OptimizationOutput>(url, input);
    return response.data;
  } catch (error: any) {
    console.error("Error optimize:", error);
    throw error;
  }
};

export const getClientIds = async (): Promise<string[]> => {
  const response = await axiosInstance.get<string[]>('/clients');
  return response.data;
};

export const getProductIds = async (): Promise<string[]> => {
  const response = await axiosInstance.get<string[]>('/products');
  return response.data;
};

// PRC-046: nombre de cada cliente o producto de la cuenta (código → nombre).
export const getEntityNames = async (kind: 'customer' | 'product'): Promise<Record<string, string>> => {
  const response = await axiosInstance.get<Record<string, string>>('/entity-names', { params: { kind } });
  return response.data ?? {};
};

export const getRetrainingStatus = async (): Promise<RetrainingStatus> => {
  try {
      const response = await axiosInstance.get<RetrainingStatus>('/retraining/status');
      return response.data;
  } catch (error: any) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
          return {
              status: 'not_found', log_id: null, started_at: null, finished_at: null, metrics: null,
              error_message: "No jobs yet.", can_train: true, next_training_available_at: new Date().toISOString()
          };
      }
      throw new Error('Error estado reentrenamiento');
  }
};

export const startRetraining = async (): Promise<RetrainingJobResponse> => {
  const response = await axiosInstance.post<RetrainingJobResponse>('/retrain');
  return response.data;
};

export const getProductAttributesFormSchema = async (): Promise<ProductAttributesFormSchemaAPIResponse> => {
  const response = await axiosInstance.get<ProductAttributesFormSchemaAPIResponse>('/form-schema/product-attributes');
  return response.data;
};

export const getClientAttributesFormSchema = async (): Promise<ClientAttributesFormSchemaAPIResponse> => {
  const response = await axiosInstance.get<ClientAttributesFormSchemaAPIResponse>('/form-schema/client-attributes');
  return response.data;
};

export const prepareOptimizationCache = async (): Promise<{ status: string }> => {
  try {
    const { data } = await axiosInstance.post('/prepare-cache', {});
    return data;
  } catch (error) {
    return { status: "cache_preparation_failed" };
  }
};

export const getOptimizationCacheStatus =
  async (): Promise<OptimizationCacheStatusResponse> => {
    const response =
      await axiosInstance.get<OptimizationCacheStatusResponse>(
        '/cache-status',
      );

    return response.data;
  };

export interface OptimizationLogEntry {
  id: number;
  optimization_id: string;
  created_at: string;
  customer_id: string | null;
  customer_name?: string | null;
  product_id: string | null;
  success: boolean;
  strategy_profile: string;
  suggested_price: number | null;
  cost_used: number | null; // <--- AÑADIDO
  suggested_margin_abs: number | null; // <--- AÑADIDO
  execution_user: string;
  full_input_payload: any;
  full_output_payload: any;
}

export const getOptimizationHistory = async (): Promise<OptimizationLogEntry[]> => {
  const response = await axiosInstance.get<OptimizationLogEntry[]>('/history', {
    params: { limit: 50 },
  });

  return response.data;
};